import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { useNavigate, useLocation } from "react-router-dom";

import { StageLinkLogo } from "../../components/Branding/StageLinkLogo";

const navLinks = [
  { label: "Home", href: "/home" },
  { label: "About", href: "/coming-soon" },
  { label: "Contact", href: "/coming-soon" },
  { label: "Explore More", href: "/coming-soon" }, 
];

const COLORS = {
  bg: "#0b0b0b",
  bar: "rgba(10,10,10,0.82)",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};

const LandingHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDrawer, setOpenDrawer] = useState(false);



  const handleDrawerToggle = () => setOpenDrawer(!openDrawer);
  const isActive = (href: string) =>
    location.pathname === href || location.hash === href;



  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.98), rgba(10,10,10,0.80))",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          // color: "#fff",
          zIndex: 1201,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{ justifyContent: "space-between", py: 1, gap: 1 }}
          >
            {/* Logo */}
            <StageLinkLogo/>

            {/* Desktop Nav */}
            {!isMobile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {navLinks.map(({ label, href }) => {
                  const active = isActive(href);
                  return (
                    <Button
                      key={label}
                      onClick={() => navigate(href)}
                      sx={{
                        position: "relative",
                        color: active ? COLORS.text : COLORS.textMuted,
                        fontWeight: 600,
                        textTransform: "none",
                        px: 1.5,
                        "&:hover": { color: COLORS.text },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 8,
                          right: 8,
                          bottom: -6,
                          height: 2,
                          borderRadius: 2,
                          backgroundColor: active ? COLORS.red : "transparent",
                          transition: "background-color .2s",
                        },
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}

                {/* PWA Install/Open Button */}
             

                {/* Auth */}
                <Button
                  onClick={() => navigate("/sign-in")}
                  sx={{
                    color: COLORS.text,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 1.5,
                    "&:hover": { color: COLORS.text },
                  }}
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  variant="contained"
                  sx={{
                    backgroundColor: COLORS.red,
                    color: "#fff",
                    fontWeight: 800,
                    textTransform: "none",
                    px: 2.5,
                    boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                    "&:hover": { backgroundColor: COLORS.redHover },
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            ) : (
              <IconButton onClick={handleDrawerToggle}>
                <MenuIcon sx={{ color: COLORS.text }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: COLORS.bg,
            color: COLORS.text,
            borderLeft: `1px solid ${COLORS.border}`,
            fontFamily: '"Lexend", sans-serif',
          },
        }}
      >
        <Box sx={{ pt: 3, px: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, mb: 1, letterSpacing: "-0.01em" }}
          >
            Menu
          </Typography>
          <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />

          <List sx={{ py: 0 }}>
            {navLinks.map(({ label, href }) => {
              const active = isActive(href);
              return (
                <ListItemButton
                  key={label}
                  onClick={() => {
                    navigate(href);
                    setOpenDrawer(false);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    "& .MuiListItemText-primary": {
                      fontWeight: active ? 800 : 600,
                      color: active ? COLORS.text : COLORS.textMuted,
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  <ListItemText primary={label} />
                </ListItemButton>
              );
            })}

            <Divider sx={{ my: 1.5, borderColor: COLORS.border }} />

            {/* <ListItemButton
              onClick={() => {
                navigate("/book");
                setOpenDrawer(false);
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                "& .MuiListItemText-primary": {
                  fontWeight: 600,
                  color: COLORS.text,
                },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
              }}
            >
              <ListItemText primary="Be A Talent" />
            </ListItemButton> */}

            <ListItemButton
              onClick={() => {
                navigate("/sign-in");
                setOpenDrawer(false);
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                "& .MuiListItemText-primary": {
                  fontWeight: 600,
                  color: COLORS.text,
                },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
              }}
            >
              <ListItemText primary="Login" />
            </ListItemButton>
          </List>

          {/* Drawer CTAs */}
          <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
            <Button
              onClick={() => {
                navigate("/register");
                setOpenDrawer(false);
              }}
              variant="contained"
              sx={{
                backgroundColor: COLORS.red,
                color: "#fff",
                fontWeight: 800,
                textTransform: "none",
                boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                "&:hover": { backgroundColor: COLORS.redHover },
              }}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default LandingHeader;
